import React, { useState } from 'react';
import { FaTasks, FaPlus, FaCheck, FaClock, FaBookmark, FaListUl } from 'react-icons/fa';
import { toast } from 'sonner';
import { axiosInstance as axios } from '../../context/AuthContext';
import { DateInput } from '../ui/DateInput';
import { SaveTemplateModal } from './templates/SaveTemplateModal';
import { TemplatePickerModal, appliedMessage } from './templates/TemplatePickerModal';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

function TasksTab({ tasks, eventId, eventName, event, familyId, canUseTemplate, familyMembers, onTasksUpdated }) {
  const [showForm, setShowForm] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const templatesEnabled = canUseTemplate && event && familyId;
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'meals',
    description: '',
    assigned_to: '',
    due_date: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTask) {
        await axios.patch(
          `${API}/tasks/${editingTask.task_id}`,
          { 
            title: formData.title,
            category: formData.category,
            description: formData.description || null,
            assigned_to: formData.assigned_to || null,
            due_date: formData.due_date || null
          },
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API}/tasks`,
          { 
            ...formData, 
            event_id: eventId, 
            assigned_to: formData.assigned_to || null,
            due_date: formData.due_date || null
          },
          { withCredentials: true }
        );
      }
      
      setShowForm(false);
      setEditingTask(null);
      setFormData({ title: '', category: 'meals', description: '', assigned_to: '', due_date: '' });
      onTasksUpdated();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      category: task.category,
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      due_date: task.due_date || ''
    });
    setShowForm(true);
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
      onTasksUpdated();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingTask(null);
    setFormData({ title: '', category: 'meals', description: '', assigned_to: '', due_date: '' });
  };

  const handleToggleStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    
    try {
      await axios.patch(
        `${API}/tasks/${taskId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      
      onTasksUpdated();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const categories = ['meals', 'transportation', 'lodging', 'activities', 'other'];
  
  const getMemberName = (userId) => {
    if (!userId) return null;
    const member = familyMembers.find(m => m.user_id === userId);
    return member ? member.name : 'Unknown';
  };

  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'completed') return false;
    const dueDate = new Date(task.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  const sortedTasks = [...tasks].sort((a, b) => {
    const aOverdue = isOverdue(a);
    const bOverdue = isOverdue(b);
    
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;
    
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;
    
    if (a.due_date && !b.due_date) return -1;
    if (!a.due_date && b.due_date) return 1;
    if (a.due_date && b.due_date) {
      return new Date(a.due_date) - new Date(b.due_date);
    }
    
    return 0;
  });

  return (
    <div data-testid="tasks-tab">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            data-testid="create-task-btn"
          >
            <FaPlus />
            <span>Add Task</span>
          </button>
          {templatesEnabled && (
            <button
              onClick={() => setShowPicker(true)}
              className="flex items-center gap-2 px-5 py-3 border border-blue-600 text-blue-700 rounded-lg hover:bg-blue-50 font-semibold transition-colors"
              data-testid="use-template-btn"
            >
              <FaListUl />
              <span>Use a Template</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-4">
          {sortedTasks.filter(t => isOverdue(t)).length > 0 && (
            <span className="text-sm text-red-600 font-medium" data-testid="overdue-count">
              {sortedTasks.filter(t => isOverdue(t)).length} overdue task(s)
            </span>
          )}
          {tasks.length > 0 && (
            <button
              onClick={() => setShowSaveTemplate(true)}
              className="flex items-center gap-2 px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 text-sm font-medium transition-colors"
              data-testid="save-as-template-btn"
            >
              <FaBookmark />
              <span>Save as Template</span>
            </button>
          )}
        </div>
      </div>

      {showSaveTemplate && (
        <SaveTemplateModal
          eventId={eventId}
          defaultName={eventName}
          taskCount={tasks.length}
          onClose={() => setShowSaveTemplate(false)}
        />
      )}

      {showPicker && (
        <TemplatePickerModal
          event={event}
          familyId={familyId}
          reopened
          onClose={() => setShowPicker(false)}
          onApplied={(data) => {
            setShowPicker(false);
            toast.success(appliedMessage(data, event.name));
            onTasksUpdated();
          }}
        />
      )}

      {tasks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <FaTasks className="text-gray-300 text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">No tasks yet</h3>
          <p className="text-gray-600">
            {templatesEnabled ? 'Add tasks one at a time, or tap "Use a Template" for a ready-made checklist.' : 'Start adding tasks to organize your event!'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedTasks.map((task) => {
            const taskOverdue = isOverdue(task);
            return (
              <div
                key={task.task_id}
                className={`rounded-lg shadow-md p-4 ${
                  taskOverdue 
                    ? 'bg-red-50 border-l-4 border-red-500' 
                    : 'bg-white'
                }`}
                data-testid={`task-${task.task_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <button
                      onClick={() => handleToggleStatus(task.task_id, task.status)}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        task.status === 'completed'
                          ? 'bg-green-600 border-green-600'
                          : 'border-gray-300 hover:border-green-600'
                      }`}
                      data-testid={`task-toggle-${task.task_id}`}
                    >
                      {task.status === 'completed' && <FaCheck className="text-white text-xs" />}
                    </button>
                    
                    <div className="flex-1">
                      <h4 className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-500' : taskOverdue ? 'text-red-800' : 'text-gray-800'}`}>
                        {task.title}
                        {taskOverdue && <span className="ml-2 text-xs font-bold text-red-600">OVERDUE</span>}
                      </h4>
                      <div className="flex items-center space-x-2 mt-1 flex-wrap">
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                          {task.category}
                        </span>
                        {task.assigned_to && (
                          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded">
                            Assigned to: {getMemberName(task.assigned_to)}
                          </span>
                        )}
                        {task.due_date && (
                          <span className={`text-xs px-2 py-1 rounded ${
                            taskOverdue 
                              ? 'bg-red-200 text-red-800 font-semibold' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        )}
                        {task.description && (
                          <span className="text-xs text-gray-600">{task.description}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEditTask(task)}
                      className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded border border-blue-300"
                      data-testid={`edit-task-${task.task_id}`}
                      title="Edit task"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteTask(task.task_id)}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded border border-red-300"
                      data-testid={`delete-task-${task.task_id}`}
                      title="Delete task"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Task Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="create-task-modal">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              {editingTask ? 'Edit Task' : 'Add New Task'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Task Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                  data-testid="task-title-input"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  data-testid="task-category-select"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Assign To
                </label>
                <select
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  data-testid="task-assign-select"
                >
                  <option value="">Unassigned</option>
                  {familyMembers.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Due Date
                </label>
                <DateInput
                  value={formData.due_date}
                  onChange={(val) => setFormData({ ...formData, due_date: val })}
                  className="border-gray-300 focus-within:ring-2 focus-within:ring-blue-600 focus-within:border-transparent"
                  data-testid="task-due-date-input"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  rows="2"
                  data-testid="task-description-input"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  data-testid="submit-task-btn"
                >
                  {editingTask ? 'Save Changes' : 'Add Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TasksTab;
